// FieldOptionsForm.js

import { useState } from 'react';
import api from '../../services/api';

const FieldOptionsForm = ({ project, onClose }) => {
  const [selectedField, setSelectedField] = useState('');
  const [fieldOptions, setFieldOptions] = useState([]);
  const [newOption, setNewOption] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');
  const [editingOptionId, setEditingOptionId] = useState(null);
  const [editingText, setEditingText] = useState('');

  // Поля, для которых можно задавать варианты
  const availableFields = [
    { value: 'group', label: 'Группа' },
    { value: 'agent_name', label: 'ФИО сотрудника' },
    { value: 'status', label: 'Статус' },
    { value: 'subject2', label: 'Subject' },
    { value: 'impact', label: 'Impact' },
    { value: 'assessment_type', label: 'Тип оценки' },
    { value: 'receipt_channel', label: 'Канал поступления'},
    { value: 'service', label: 'Сервис' },
    { value: 'os_specialist', label: 'Специалист ОС' }
  ];

  // Загрузка вариантов поля при выборе поля
  const loadFieldOptions = (field) => {
    setLoading(true);
    setError(null);
    setSuccess('');
    api.get(`/field-options?project_id=${project.id}&field_name=${field}`)
      .then((res) => {
        console.log(res)
        const sorted = res.data ? res.data.data.sort((a, b) => a.position - b.position): [];
        setFieldOptions(sorted);
      })
      .catch((err) => {
        console.error('Ошибка загрузки вариантов поля:', err);
        setError('Не удалось загрузить варианты поля');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleFieldChange = (e) => {
    const value = e.target.value;
    setSelectedField(value);
    if (value) {
      loadFieldOptions(value);
    } else {
      setFieldOptions([]);
      setNewOption('');
      setError(null);
      setSuccess('');
    }
  };

  const handleAddOption = () => {
    const trimmed = newOption.trim();
    if (!trimmed) return;

    setLoading(true);
    setError(null);
    setSuccess('');

    // Создаем новый вариант
    const newOpt = {
      project_id: project.id,
      field_name: selectedField,
      option_value: trimmed,
      option_text: trimmed,
      position: fieldOptions.length
    };

    // Отправляем запрос на создание нового варианта
    api.post('/field-options', newOpt)
      .then((res) => {
        // Добавляем созданный вариант в список
        const createdOption = res.data.data;
        setFieldOptions([...fieldOptions, createdOption]);
        setNewOption('');
        setSuccess('Вариант успешно добавлен');
      })
      .catch((err) => {
        console.error('Ошибка добавления варианта:', err);
        setError('Не удалось добавить вариант: ' + (err.response?.data?.message || ''));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleRemoveOption = (optionId) => {
    api.delete(`/field-options/${optionId}`)
      .then(() => {
        setFieldOptions(fieldOptions.filter(opt => opt.id !== optionId));
        setSuccess('Вариант успешно удален');
      })
      .catch((err) => {
        console.error('Ошибка удаления варианта:', err);
        setError('Не удалось удалить вариант: ' + (err.response?.data?.message || ''));
      });
  };

  const handleEditClick = (option) => {
    setEditingOptionId(option.id);
    setEditingText(option.option_text);
  };

  const handleEditSave = (optionId) => {
    const trimmed = editingText.trim();
    if (!trimmed) {
      setError('Текст варианта не может быть пустым');
      return;
    }

    // Находим элемент в массиве
    const option = fieldOptions.find(opt => opt.id === optionId);
    if (!option) return;

    // Отправляем PATCH-запрос на сервер
    api.patch(`/field-options/${optionId}`, {
      option_value: trimmed,
      option_text: trimmed
    })
    .then((res) => {
      // Обновляем данные в состоянии после успешного ответа
      const updatedOptions = fieldOptions.map(opt => 
        opt.id === optionId 
          ? { ...opt, option_text: trimmed, option_value: trimmed }
          : opt
      );
      setFieldOptions(updatedOptions);
      setEditingOptionId(null);
      setEditingText('');
      setSuccess(res.data.message || 'Вариант успешно обновлен');
    })
    .catch((err) => {
      console.error('Ошибка обновления варианта:', err);
      setError('Не удалось обновить вариант: ' + (err.response?.data?.message || ''));
    });
  };

  const handleEditCancel = () => {
    setEditingOptionId(null);
    setEditingText('');
  };

  const handleSave = () => {
    if (!selectedField) {
      setError('Выберите поле');
      return;
    }

    if (fieldOptions.length === 0) {
      setError('Нельзя сохранить пустой список вариантов');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess('');

    const payload = {
      project_id: project.id,
      field_name: selectedField,
      options: fieldOptions.map((option, index) => ({
        ...option,
        position: index
      }))
    };
    console.log(payload)
    api.post('/field-options/bulk', payload)
      .then(() => {
        setSuccess('Варианты успешно сохранены');
        const updatedOptions = fieldOptions.map((option, index) => ({
          ...option,
          position: index
        }));
        setFieldOptions(updatedOptions);
      })
      .catch((err) => {
        console.error('Ошибка сохранения вариантов:', err);
        setError('Не удалось сохранить варианты: ' + (err.response?.data?.message || ''));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div className="field-options-form">
      <h3>Варианты полей для проекта "{project.name}"</h3>
      
      {error && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="form-group">
        <label>Выберите поле:</label>
        <select 
          className="form-control"
          value={selectedField}
          onChange={handleFieldChange}
          disabled={loading}
        >
          <option value="">-- Выберите поле --</option>
          {availableFields.map((field) => (
            <option key={field.value} value={field.value}>
              {field.label}
            </option>
          ))}
        </select>
      </div>

      {selectedField && (
        <>
          <div className="form-group mt-3">
            <label>Новый вариант:</label>
            <div className="input-group">
              <input
                type="text"
                className="form-control"
                value={newOption}
                onChange={(e) => setNewOption(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddOption()}
                disabled={loading}
              />
              <button
                className="btn btn-primary"
                type="button"
                onClick={handleAddOption}
                disabled={loading}
              >
                Добавить
              </button>
            </div>
          </div>

          <div className="options-list mt-3">
            <h5>Текущие варианты:</h5>
            {fieldOptions.length === 0 ? (
              <p className="text-muted">Нет добавленных вариантов</p>
            ) : (
              <ul className="list-group">
                {fieldOptions.map((option) => (
                  <li key={option.id} className="list-group-item d-flex justify-content-between align-items-center">
                    {editingOptionId === option.id ? (
                      <div className="d-flex align-items-center" style={{gap: '8px'}}>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={editingText}
                          onChange={(e) => setEditingText(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && handleEditSave(option.id)}
                          onBlur={() => handleEditSave(option.id)}
                          autoFocus
                        />
                        <button
                          className="btn btn-sm btn-success"
                          onClick={() => handleEditSave(option.id)}
                          disabled={loading}
                        >
                          Сохранить
                        </button>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={handleEditCancel}
                          disabled={loading}
                        >
                          Отмена
                        </button>
                      </div>
                    ) : (
                      <span 
                        onDoubleClick={() => handleEditClick(option)}
                        style={{cursor: 'pointer', flex: 1}}
                      >
                        {option.option_text}
                      </span>
                    )}
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleRemoveOption(option.id)}
                      disabled={loading}
                    >
                      Удалить
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="mt-4">
            <button 
              className="btn btn-success me-2"
              onClick={handleSave}
              disabled={loading}
            >
              {loading ? 'Сохранение...' : 'Сохранить все варианты'}
            </button>
            <button 
              className="btn btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Закрыть
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default FieldOptionsForm;